import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { FieldCheckbox } from '../../../components';
import css from './SelectMultipleFilter.module.css';
import { transformArrayKeys } from '../../../util/genericHelpers';
import {
  buildGroupedSizeFilterOptions,
  getChildAndAdultsOptions,
} from '../../../util/searchPageHelpers';
import { useConfiguration } from '../../../context/configurationContext';
import { getListingFieldOptions } from '../../../config/marketplaceConfig';
import { ListingFieldConfigKey } from '../../../util/enums';

// Helper to ensure every item has an 'option' property for SizeList
// This maps 'key' (from SDK/Config) to 'option' (expected by your UI)
const normalizeForUI = item => ({
  ...item,
  option: item.option || item.key || item.label, // Fallback chain to ensure a value exists
  label: item.label,
});

const SizeList = ({ id, name, options, containerClass, selectedCategory }) => {
  // Guard Clause: Prevent crashes if data is missing
  if (!options || options.length === 0) return null;

  return (
    <div className={css.sizeSec}>
      <fieldset className={containerClass}>
        <ul className={css.list}>
          {options.map(optionConfig => {
            const { option, label } = optionConfig;

            // CRITICAL FIX: Skip items where the value is missing to prevent duplicate key errors
            if (!option) return null;

            // Generate a robust unique key
            const fieldId = `${id}.${selectedCategory}.${option}`;

            return (
              <li
                key={fieldId}
                className={classNames(
                  css.item,
                  // Apply pill styling only for specific categories
                  ['Adult', 'Child'].includes(selectedCategory) ? css.sizePill : null
                )}
              >
                <FieldCheckbox
                  id={fieldId}
                  name={name}
                  label={<span className={css.colorLabel}>{label}</span>}
                  value={option}
                />
              </li>
            );
          })}
        </ul>
      </fieldset>
    </div>
  );
};

function RenderSizes({ id, name, className, options, containerClass }) {
  const [selectedCategory, setSelectedCategory] = useState('Adult');
  const config = useConfiguration();
  const configuredShoesOptions = getListingFieldOptions(ListingFieldConfigKey.SHOES_SIZE, {
    config,
  });

  // PERFORMANCE FIX: useMemo ensures this heavy logic runs ONLY when 'options' prop changes
  const categoryOptions = useMemo(() => {
    // 1. Process and Normalize 'Options' (from props)
    // We map immediately to ensure 'option' property exists for the filter functions
    const rawSizes = transformArrayKeys(options || []).map(normalizeForUI);
    const groupedSizes = buildGroupedSizeFilterOptions(rawSizes).options;

    // 2. Filter Adult/Child options
    // Now that rawSizes have 'option' property, your getChildAndAdultsOptions helper will work correctly
    const { childOptions, adultOptions } = getChildAndAdultsOptions(groupedSizes);

    // 3. Process Shoes
    // Transform and Normalize
    const rawShoes = transformArrayKeys(configuredShoesOptions || []).map(normalizeForUI);
    const groupedShoes = buildGroupedSizeFilterOptions(rawShoes).options;

    return {
      Adult: adultOptions,
      Child: childOptions,
      Shoes: groupedShoes,
    };
  }, [options, configuredShoesOptions]);

  const handleTabClick = category => e => {
    e.preventDefault(); // Prevent form submission
    setSelectedCategory(category);
  };

  return (
    <div className={classNames(css.filterContainer, className)}>
      {/* Tabs */}
      <div className={css.tabs}>
        {['Adult', 'Child', 'Shoes'].map(category => (
          <button
            key={category}
            type="button" // Explicitly prevent form submission behavior
            className={classNames(css.tab, {
              [css.activeTab]: selectedCategory === category,
            })}
            onClick={handleTabClick(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* List */}
      <SizeList
        id={id}
        name={name}
        options={categoryOptions[selectedCategory]}
        containerClass={containerClass}
        selectedCategory={selectedCategory}
      />
    </div>
  );
}

export default RenderSizes;
